import { ServiceExpensesService } from '@http/clients/reporting-app/service-expenses.service';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { formatCurrencyCompact, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import { WidgetType } from '../../types/report-widget-type.enum';
import {
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    projectDueDateEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    projectStatusEditorConfig
} from './shared/additional-filters';
import { salesMainFilters } from './shared/main-filters';

export const ServiceModuleExpenses: IReportConfiguration<ServiceExpensesService> = {
    base: {
        type: ReportType.ServiceModuleExpenses,
        serviceType: ServiceExpensesService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Avg' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [
                        {
                            name: 'SellPrice',
                            label: 'Sell Price ($)',
                            format: 'currency'
                        },
                        {
                            name: 'ExpenseCost',
                            label: 'Expense Cost ($)',
                            format: 'currency'
                        },
                        {
                            name: 'DirectCost',
                            label: 'DC ($)',
                            format: 'currency'
                        },
                        {
                            name: 'DirectCostPercentage',
                            label: 'DC (%)',
                            format: 'percent'
                        },
                        {
                            name: 'TotalDirectCostPercentage',
                            label: 'TDC (%)',
                            format: 'percent'
                        }
                    ]
                }
            },
            {
                methodName: 'getLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Expenses by Total Sell Price'
                    },
                    series: [
                        {
                            xKey: 'expenseType',
                            yName: 'Total Sell Price ($)',
                            yKey: 'totalSellPrice'
                        },
                        {
                            xKey: 'expenseType',
                            yName: 'Total Expense Cost ($)',
                            yKey: 'totalExpenseCost'
                        },
                        {
                            xKey: 'expenseType',
                            yName: 'AVG DC (%)',
                            yKey: 'avgDirectCostPercentage'
                        },
                        {
                            xKey: 'expenseType',
                            yName: 'AVG TDC (%)',
                            yKey: 'avgTotalDirectCostPercentage'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['totalSellPrice', 'totalExpenseCost'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['avgDirectCostPercentage', 'avgTotalDirectCostPercentage']
                        }
                    ]
                }
            },
            {
                methodName: 'getColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yName: 'Total Sell Price ($)',
                            yKey: 'totalSellPrice'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yName: 'Total Expense Cost ($)',
                            yKey: 'totalExpenseCost'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['totalSellPrice', 'totalExpenseCost'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    { field: 'expenseType', headerName: 'Expense Type', enableRowGroup: true, lockVisible: true },
                    { field: 'totalSellPrice', headerName: 'Total Sell Price', type: 'currency', aggFunc: 'sum' },
                    { field: 'totalExpenseCost', headerName: 'Total Expense Cost', type: 'currency', aggFunc: 'sum' },
                    { field: 'avgExpenseCost', headerName: 'Avg Expense Cost', type: 'currency', aggFunc: 'sum' },
                    { field: 'minExpenseCost', headerName: 'Min Expense Cost', type: 'currency', aggFunc: 'sum' },
                    { field: 'medExpenseCost', headerName: 'Med Expense Cost', type: 'currency', aggFunc: 'sum' },
                    { field: 'maxExpenseCost', headerName: 'Max Expense Cost', type: 'currency', aggFunc: 'sum' },
                    { field: 'avgDirectCost', headerName: 'Avg DC ($)', type: 'currency', aggFunc: 'sum' },
                    { field: 'minDirectCost', headerName: 'Min DC ($)', type: 'currency', aggFunc: 'sum' },
                    { field: 'medDirectCost', headerName: 'Med DC ($)', type: 'currency', aggFunc: 'sum' },
                    { field: 'maxDirectCost', headerName: 'Max DC ($)', type: 'currency', aggFunc: 'sum' },
                    { field: 'avgDirectCostPercentage', headerName: 'Avg DC (%)', type: 'percentage', aggFunc: 'avg' },
                    { field: 'minDirectCostPercentage', headerName: 'Min DC (%)', type: 'percentage', aggFunc: 'avg' },
                    { field: 'medDirectCostPercentage', headerName: 'Med DC (%)', type: 'percentage', aggFunc: 'avg' },
                    { field: 'maxDirectCostPercentage', headerName: 'Max DC (%)', type: 'percentage', aggFunc: 'avg' },
                    {
                        field: 'avgTotalDirectCostPercentage',
                        headerName: 'Avg TDC (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minTotalDirectCostPercentage',
                        headerName: 'Min TDC (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'medTotalDirectCostPercentage',
                        headerName: 'Med TDC (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'maxTotalDirectCostPercentage',
                        headerName: 'Max TDC (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...salesMainFilters],
        additional: [
            projectDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            projectStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig
        ]
    }
};
